// src/firebase/dangerZone.js
// Destructive account operations for the Settings "Danger Zone":
// partial learning-data removal and full account reset. Complete account
// deletion is intentionally NOT self-service — it's handled manually by the
// owner (billing cancellation + Admin SDK purge) via the deletion contact email.
import { collection, doc, getDocs, limit, query, writeBatch } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import { buildDefaultMetadataDocs } from './user';

// Firestore batches cap at 500 ops; stay under it.
const BATCH_SIZE = 450;

const metadataDocRef = (userId, docId) => doc(firestore, 'users', userId, 'metadata', docId);

/**
 * Delete every document in a collection in batches.
 * @param {import('firebase/firestore').CollectionReference} colRef
 * @param {(deletedSoFar: number) => void} [onProgress]
 * @returns {Promise<number>} total documents deleted
 */
const deleteAllDocs = async (colRef, onProgress) => {
  let total = 0;
  for (;;) {
    const snap = await getDocs(query(colRef, limit(BATCH_SIZE)));
    if (snap.empty) break;
    const batch = writeBatch(firestore);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
    total += snap.size;
    onProgress?.(total);
    if (snap.size < BATCH_SIZE) break;
  }
  return total;
};

/**
 * Remove all learned words and/or sentences and zero the aggregates derived
 * from the items (counts_by_level, counts_by_srs, review_accuracy, the ids
 * array, and listening_stats — a denormalized sum of duration × listens per
 * item, so it must fall to zero with the items). The append-only daily logs
 * (learned_today, reviewed_*_today, played_*_today) are intentionally kept,
 * so the heatmap/streak history survives a partial removal.
 * @param {string} userId
 * @param {{words?: boolean, sentences?: boolean}} scope
 * @param {(progress: {phase: string, count: number}) => void} [onProgress]
 */
export const removeLearningData = async (userId, scope, onProgress) => {
  const defaults = buildDefaultMetadataDocs();

  if (scope.words) {
    await deleteAllDocs(collection(firestore, 'users', userId, 'user_words'), (count) =>
      onProgress?.({ phase: 'words', count })
    );
  }
  if (scope.sentences) {
    await deleteAllDocs(collection(firestore, 'users', userId, 'user_sentences'), (count) =>
      onProgress?.({ phase: 'sentences', count })
    );
  }

  const batch = writeBatch(firestore);
  if (scope.words) {
    batch.set(metadataDocRef(userId, 'user_words_ids'), { data: [] });
    batch.update(metadataDocRef(userId, 'counts_by_level'), { words: defaults.counts_by_level.words });
    batch.update(metadataDocRef(userId, 'counts_by_srs'), { words: defaults.counts_by_srs.words });
    batch.update(metadataDocRef(userId, 'review_accuracy'), { words: defaults.review_accuracy.words });
    batch.update(metadataDocRef(userId, 'listening_stats'), { words: defaults.listening_stats.words });
  }
  if (scope.sentences) {
    batch.set(metadataDocRef(userId, 'user_sentences_ids'), { data: [] });
    batch.update(metadataDocRef(userId, 'counts_by_level'), {
      sentences_by_wk: defaults.counts_by_level.sentences_by_wk,
    });
    batch.update(metadataDocRef(userId, 'counts_by_srs'), { sentences: defaults.counts_by_srs.sentences });
    batch.update(metadataDocRef(userId, 'review_accuracy'), { sentences: defaults.review_accuracy.sentences });
    batch.update(metadataDocRef(userId, 'listening_stats'), { sentences: defaults.listening_stats.sentences });
  }
  await batch.commit();
};

/**
 * Full account reset: delete all learned items and restore every metadata doc
 * to its registration default — including the activity/history docs, so the
 * streak, heatmap, and listening stats start from zero. The user document
 * itself (email, settings, tour flags) and any subscription are untouched.
 * @param {string} userId
 * @param {(progress: {phase: string, count: number}) => void} [onProgress]
 */
export const resetAccountData = async (userId, onProgress) => {
  await deleteAllDocs(collection(firestore, 'users', userId, 'user_words'), (count) =>
    onProgress?.({ phase: 'words', count })
  );
  await deleteAllDocs(collection(firestore, 'users', userId, 'user_sentences'), (count) =>
    onProgress?.({ phase: 'sentences', count })
  );

  const batch = writeBatch(firestore);
  for (const [docId, data] of Object.entries(buildDefaultMetadataDocs())) {
    batch.set(metadataDocRef(userId, docId), data);
  }
  await batch.commit();
};
